import { Children, isValidElement, type FC, type ReactNode } from 'react';
import { useRef } from 'react';
import { useSectionActive } from '@/hooks/useHorizontalPager';
import styles from './HorizontalPager.module.css';

interface SectionPagerProps {
  children: ReactNode;
}

/**
 * Plain stacked scroller for the home page.
 *
 * Sections flow naturally — no sticky pinning, no card stacking, no
 * overlap. The user scrolls through each section in document order.
 *
 * `useSectionActive` attaches an IntersectionObserver to each section and
 * sets `data-active="true"` when it enters the viewport, so entrance
 * animations in `useFadeUpOnScroll` and `AnimatedNumber` still fire
 * exactly once per section.
 */
export const HorizontalPager: FC<SectionPagerProps> = ({ children }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  useSectionActive(trackRef);

  return (
    <div className={styles.root}>
      <div ref={trackRef} className={styles.track}>
        {Children.map(children, (child) => {
          if (!isValidElement(child)) return child;
          return child;
        })}
      </div>
    </div>
  );
};

HorizontalPager.displayName = 'HorizontalPager';
